/**
 * Google Analytics 4 wrapper with custom events
 * Privacy-friendly tracking for portfolio interactions
 */

class Analytics {
  constructor() {
    this.isEnabled = false;
    this.debugMode = import.meta.env.DEV;
  }

  /**
   * Initialize GA4 tracking
   * @param {string} measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
   */
  init(measurementId) {
    if (!measurementId || measurementId === 'YOUR-GA-ID') {
      console.warn('[Analytics] No valid GA4 ID provided. Analytics disabled.');
      return;
    }

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: true,
      anonymize_ip: true, // GDPR compliance
      cookie_flags: 'SameSite=None;Secure'
    });

    this.isEnabled = true;
    this.log('Analytics initialized', measurementId);
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name
   * @param {object} params - Event parameters
   */
  trackEvent(eventName, params = {}) {
    if (!this.isEnabled) {
      this.log('Event not tracked (analytics disabled):', eventName, params);
      return;
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
      this.log('Event tracked:', eventName, params);
    }
  }

  /**
   * Track project view
   * @param {string} projectName - Project name
   * @param {string} category - Project category
   */
  trackProjectView(projectName, category) {
    this.trackEvent('project_view', {
      project_name: projectName,
      project_category: category,
      event_category: 'engagement'
    });
  }

  /**
   * Track project link click
   * @param {string} projectName - Project name
   * @param {string} linkType - 'github', 'demo', 'paper'
   */
  trackProjectClick(projectName, linkType) {
    this.trackEvent('project_click', {
      project_name: projectName,
      link_type: linkType,
      event_category: 'engagement'
    });
  }

  /**
   * Track CV download
   */
  trackCVDownload() {
    this.trackEvent('cv_download', {
      event_category: 'conversion',
      event_label: 'CV Download'
    });
  }

  /**
   * Track contact form submission
   * @param {string} subject - Form subject
   */
  trackFormSubmission(subject) {
    this.trackEvent('form_submit', {
      form_name: 'contact',
      subject: subject,
      event_category: 'conversion'
    });
  }

  /**
   * Track navigation clicks
   * @param {string} section - Section name
   */
  trackNavigation(section) {
    this.trackEvent('navigation_click', {
      section_name: section,
      event_category: 'navigation'
    });
  }

  /**
   * Track social media clicks
   * @param {string} platform - 'github', 'linkedin', 'twitter'
   */
  trackSocialClick(platform) {
    this.trackEvent('social_click', {
      platform: platform,
      event_category: 'engagement'
    });
  }

  /**
   * Track certification clicks
   * @param {string} certificationName - Certification name
   * @param {string} issuer - Issuer name
   */
  trackCertificationClick(certificationName, issuer) {
    this.trackEvent('certification_click', {
      certification_name: certificationName,
      issuer: issuer,
      event_category: 'engagement'
    });
  }

  /**
   * Track scroll depth
   * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
   */
  trackScrollDepth(percentage) {
    this.trackEvent('scroll_depth', {
      scroll_percentage: percentage,
      event_category: 'engagement'
    });
  }

  /**
   * Track theme toggle
   * @param {string} theme - 'light' or 'dark'
   */
  trackThemeToggle(theme) {
    this.trackEvent('theme_toggle', {
      theme_mode: theme,
      event_category: 'ui_interaction'
    });
  }

  /**
   * Track filter usage
   * @param {string} filterType - Type of filter
   * @param {string} filterValue - Selected filter value
   */
  trackFilterUse(filterType, filterValue) {
    this.trackEvent('filter_use', {
      filter_type: filterType,
      filter_value: filterValue,
      event_category: 'ui_interaction'
    });
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.debugMode) {
      console.log('[Analytics]', ...args);
    }
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Auto-init from environment variable or meta tag
if (typeof window !== 'undefined') {
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID ||
               document.querySelector('meta[name="ga-measurement-id"]')?.content;

  if (gaId) {
    analytics.init(gaId);
  }
}
